import * as React from "react";
import {useState} from "react";
import {Link} from "react-router";
import {useIntl} from "react-intl";
import Panel from "../Panel";
import {changeBetween} from "../../../helper/analysisChange";

export type RankingEntry = {
    key: string
    label: string
    amount: number // in the ranked period
    previousAmount: number | null // in the period before, 0 if the entry did not exist, null if it is unknown
    href?: string
};

type RankingListProps = {
    title: string
    entries: ReadonlyArray<RankingEntry>
    formatAmount: (amount: number) => string
    increaseIsGood: boolean // colors an increase green for revenue and red for costs
    emptyLabel: string
    newLabel: string // for entries which had no amount in the period before
    goneLabel: string // for entries which have no amount in the ranked period, but had one before
    summary?: React.ReactNode // below the title, e.g. the share of the top entries
    barColor?: string // CSS color of the share bars
    limit?: number
};

/**
 * The entries of a period ranked by amount, each with a bar for its share, the change to the
 * period before and a link. Entries which dropped out are listed after the others with zero and
 * a marker, so they do not just vanish from the list.
 */
const RankingList: React.FC<RankingListProps> = ({title, entries, formatAmount, increaseIsGood, emptyLabel, newLabel, goneLabel, summary, barColor = '#3b82f6', limit = 15}) => {
    const intl = useIntl();
    const [showAll, setShowAll] = useState(false);

    const ranked = entries
        .filter(entry => entry.amount !== 0 || (entry.previousAmount ?? 0) !== 0)
        .sort((a, b) => (b.amount - a.amount) || ((b.previousAmount ?? 0) - (a.previousAmount ?? 0)) || a.label.localeCompare(b.label));
    const total = ranked.reduce((sum, entry) => sum + Math.max(0, entry.amount), 0);
    const visible = showAll ? ranked : ranked.slice(0, limit);

    const renderChange = (entry: RankingEntry) => {
        if (entry.previousAmount === null) {
            return null;
        }
        if (entry.amount === 0) {
            return <span className="text-xs text-red-600 whitespace-nowrap">{goneLabel}</span>;
        }
        if (entry.previousAmount === 0) {
            return <span className="text-xs text-green-600 whitespace-nowrap">{newLabel}</span>;
        }
        const {delta, percent} = changeBetween(entry.amount, entry.previousAmount);
        if (delta === 0) {
            return <span className="text-xs text-gray-400 whitespace-nowrap">±0 %</span>;
        }
        const isGood = (delta > 0) === increaseIsGood;
        const deltaLabel = (delta > 0 ? '+' : '') + formatAmount(delta);
        return (
            <span className={`text-xs whitespace-nowrap ${isGood ? 'text-green-600' : 'text-red-600'}`} title={deltaLabel}>
                {percent === null ? deltaLabel : intl.formatNumber(percent, {style: 'percent', maximumFractionDigits: 1, signDisplay: 'exceptZero'})}
            </span>
        );
    };

    return (
        <Panel headline={title}>
            {ranked.length === 0 ? (
                <p className="text-sm text-gray-500">{emptyLabel}</p>
            ) : (
                <div className="space-y-3">
                    {summary && <div className="text-sm text-gray-500">{summary}</div>}
                    {visible.map(entry => {
                        const share = total > 0 ? Math.max(0, entry.amount) / total : 0;
                        return (
                            <div key={entry.key}>
                                <div className="flex justify-between gap-4 text-sm">
                                    <span className={`truncate ${entry.amount === 0 ? 'text-gray-400' : 'text-gray-900'}`}>
                                        {entry.href !== undefined
                                            ? <Link to={entry.href} className="hover:underline hover:text-blue-700">{entry.label}</Link>
                                            : entry.label}
                                    </span>
                                    <span className="flex items-baseline gap-2 whitespace-nowrap">
                                        {renderChange(entry)}
                                        <span className="text-gray-900 font-medium tabular-nums">
                                            {formatAmount(entry.amount)} <span className="text-gray-400">({Math.round(share * 100)} %)</span>
                                        </span>
                                    </span>
                                </div>
                                <div className="mt-1 h-1.5 bg-gray-100 rounded">
                                    <div className="h-1.5 rounded" style={{width: `${share * 100}%`, minWidth: entry.amount > 0 ? '2px' : undefined, backgroundColor: barColor}}/>
                                </div>
                            </div>
                        );
                    })}
                    {ranked.length > limit && (
                        <button type="button" onClick={() => setShowAll(previous => !previous)} className="text-sm text-blue-700 hover:underline">
                            {intl.formatMessage({id: showAll ? 'component.analysis.ranking.showLess' : 'component.analysis.ranking.showAll'}, {count: ranked.length})}
                        </button>
                    )}
                </div>
            )}
        </Panel>
    );
};

export default RankingList;
